"use client"

import { useState, useCallback } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Checkbox } from "@/components/ui/checkbox"
import { Copy, Check } from "lucide-react"
import { toast } from "sonner"
import { cn } from "@/lib/utils"

interface Permission {
  read: boolean
  write: boolean
  execute: boolean
}

interface Permissions {
  owner: Permission
  group: Permission
  others: Permission
}

const defaultPermissions: Permissions = {
  owner: { read: true, write: true, execute: false },
  group: { read: true, write: false, execute: false },
  others: { read: true, write: false, execute: false },
}

function permissionToOctal(perm: Permission): number {
  return (perm.read ? 4 : 0) + (perm.write ? 2 : 0) + (perm.execute ? 1 : 0)
}

function octalToPermission(octal: number): Permission {
  return {
    read: (octal & 4) !== 0,
    write: (octal & 2) !== 0,
    execute: (octal & 1) !== 0,
  }
}

function permissionsToOctalString(perms: Permissions): string {
  return `${permissionToOctal(perms.owner)}${permissionToOctal(perms.group)}${permissionToOctal(perms.others)}`
}

function permissionToSymbolic(perm: Permission): string {
  return `${perm.read ? "r" : "-"}${perm.write ? "w" : "-"}${perm.execute ? "x" : "-"}`
}

function permissionsToSymbolicString(perms: Permissions): string {
  return `${permissionToSymbolic(perms.owner)}${permissionToSymbolic(perms.group)}${permissionToSymbolic(perms.others)}`
}

function octalStringToPermissions(octal: string): Permissions | null {
  if (!/^[0-7]{3}$/.test(octal)) return null

  return {
    owner: octalToPermission(parseInt(octal[0], 10)),
    group: octalToPermission(parseInt(octal[1], 10)),
    others: octalToPermission(parseInt(octal[2], 10)),
  }
}

const commonPermissions = [
  { octal: "755", description: "Owner rwx, Group rx, Others rx (directories, executables)" },
  { octal: "644", description: "Owner rw, Group r, Others r (regular files)" },
  { octal: "777", description: "Everyone rwx (full access - use with caution)" },
  { octal: "700", description: "Owner rwx only (private)" },
  { octal: "600", description: "Owner rw only (private files)" },
  { octal: "666", description: "Everyone rw (no execute)" },
  { octal: "750", description: "Owner rwx, Group rx (shared executables)" },
  { octal: "640", description: "Owner rw, Group r (shared files)" },
]

export function ChmodCalculator() {
  const [permissions, setPermissions] = useState<Permissions>(defaultPermissions)
  const [octalInput, setOctalInput] = useState(() => permissionsToOctalString(defaultPermissions))
  const [copied, setCopied] = useState<string | null>(null)

  const octalString = permissionsToOctalString(permissions)
  const symbolicString = permissionsToSymbolicString(permissions)

  // Update from octal input
  const handleOctalChange = useCallback((value: string) => {
    setOctalInput(value)
    const perms = octalStringToPermissions(value)
    if (perms) {
      setPermissions(perms)
    }
  }, [])

  const togglePermission = (
    role: keyof Permissions,
    perm: keyof Permission
  ) => {
    const nextPermissions = {
      ...permissions,
      [role]: {
        ...permissions[role],
        [perm]: !permissions[role][perm],
      },
    }
    setPermissions(nextPermissions)
    setOctalInput(permissionsToOctalString(nextPermissions))
  }

  const applyPreset = (octal: string) => {
    const perms = octalStringToPermissions(octal)
    if (perms) {
      setPermissions(perms)
      setOctalInput(octal)
    }
  }

  const copyValue = async (value: string, label: string) => {
    await navigator.clipboard.writeText(value)
    setCopied(label)
    setTimeout(() => setCopied(null), 2000)
    toast.success(`Copied ${label}`)
  }

  const roles: { key: keyof Permissions; label: string }[] = [
    { key: "owner", label: "Owner" },
    { key: "group", label: "Group" },
    { key: "others", label: "Others" },
  ]

  const permTypes: { key: keyof Permission; label: string; value: number }[] = [
    { key: "read", label: "Read", value: 4 },
    { key: "write", label: "Write", value: 2 },
    { key: "execute", label: "Execute", value: 1 },
  ]

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Chmod Calculator</CardTitle>
          <CardDescription>
            Calculate Unix file permissions in octal and symbolic notation
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Octal Input */}
          <div className="space-y-2">
            <Label htmlFor="octal">Octal Value</Label>
            <div className="flex gap-2">
              <Input
                id="octal"
                type="text"
                placeholder="755"
                value={octalInput}
                onChange={(e) => handleOctalChange(e.target.value)}
                maxLength={3}
                className="font-mono text-lg w-24"
              />
              <Button
                variant="outline"
                size="icon"
                onClick={() => copyValue(octalString, "octal")}
              >
                {copied === "octal" ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Permission Matrix */}
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left py-2 pr-4"></th>
                  {permTypes.map((perm) => (
                    <th key={perm.key} className="text-center py-2 px-4">
                      <div className="font-medium">{perm.label}</div>
                      <div className="text-xs text-muted-foreground">({perm.value})</div>
                    </th>
                  ))}
                  <th className="text-center py-2 px-4">Value</th>
                </tr>
              </thead>
              <tbody>
                {roles.map((role) => (
                  <tr key={role.key} className="border-b">
                    <td className="py-3 pr-4 font-medium">{role.label}</td>
                    {permTypes.map((perm) => (
                      <td key={perm.key} className="text-center py-3 px-4">
                        <Checkbox
                          checked={permissions[role.key][perm.key]}
                          onCheckedChange={() => togglePermission(role.key, perm.key)}
                        />
                      </td>
                    ))}
                    <td className="text-center py-3 px-4 font-mono">
                      {permissionToOctal(permissions[role.key])}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Results */}
          <div className="grid sm:grid-cols-2 gap-4">
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-muted-foreground">Octal</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyValue(`chmod ${octalString}`, "chmod octal")}
                >
                  {copied === "chmod octal" ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
              <code className="text-lg font-mono">chmod {octalString}</code>
            </div>
            <div className="p-4 rounded-lg bg-muted/50">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-muted-foreground">Symbolic</Label>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => copyValue(`-${symbolicString}`, "symbolic")}
                >
                  {copied === "symbolic" ? (
                    <Check className="h-3 w-3" />
                  ) : (
                    <Copy className="h-3 w-3" />
                  )}
                </Button>
              </div>
              <code className="text-lg font-mono">-{symbolicString}</code>
            </div>
          </div>

          {/* Visual representation */}
          <div className="p-4 rounded-lg bg-muted/50 font-mono text-center">
            <div className="text-2xl tracking-wider">
              <span className="text-muted-foreground">-</span>
              <span className={cn(permissions.owner.read ? "text-green-500" : "text-muted-foreground")}>
                {permissions.owner.read ? "r" : "-"}
              </span>
              <span className={cn(permissions.owner.write ? "text-yellow-500" : "text-muted-foreground")}>
                {permissions.owner.write ? "w" : "-"}
              </span>
              <span className={cn(permissions.owner.execute ? "text-red-500" : "text-muted-foreground")}>
                {permissions.owner.execute ? "x" : "-"}
              </span>
              <span className={cn(permissions.group.read ? "text-green-500" : "text-muted-foreground")}>
                {permissions.group.read ? "r" : "-"}
              </span>
              <span className={cn(permissions.group.write ? "text-yellow-500" : "text-muted-foreground")}>
                {permissions.group.write ? "w" : "-"}
              </span>
              <span className={cn(permissions.group.execute ? "text-red-500" : "text-muted-foreground")}>
                {permissions.group.execute ? "x" : "-"}
              </span>
              <span className={cn(permissions.others.read ? "text-green-500" : "text-muted-foreground")}>
                {permissions.others.read ? "r" : "-"}
              </span>
              <span className={cn(permissions.others.write ? "text-yellow-500" : "text-muted-foreground")}>
                {permissions.others.write ? "w" : "-"}
              </span>
              <span className={cn(permissions.others.execute ? "text-red-500" : "text-muted-foreground")}>
                {permissions.others.execute ? "x" : "-"}
              </span>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Common Permissions */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Common Permissions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-2 gap-2">
            {commonPermissions.map((preset) => (
              <Button
                key={preset.octal}
                variant="outline"
                className="h-auto py-2 justify-start text-left"
                onClick={() => applyPreset(preset.octal)}
              >
                <span className="font-mono font-bold mr-2">{preset.octal}</span>
                <span className="text-xs text-muted-foreground truncate">
                  {preset.description}
                </span>
              </Button>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
